import type { ExpertWorkspaceWebSocketMessagePayload } from "../types";

/**
 * Should handle Socket.IO message if it matches the expected event format.
 *
 * Expected Socket.IO status codes/events:
 *  - 0: 'connect' - Connection established
 *  - 1: 'disconnect' - Disconnected by client or server
 *  - 2: 'event' - Event message
 *  - 3: 'ack' - Acknowledgment
 *  - 4: 'error' - Error message
 *  - 40: 'connect' packet indicating namespace connection
 *  - 41: 'disconnect' packet indicating namespace disconnection
 *  - 42: 'event' packet (most ExpertWorkspace messages)
 *  - 43: 'ack' packet with namespace
 *  - 44: 'error' packet with namespace
 *
 * For our usage, 42 (event packet) is the expected status code for incoming data messages.
 */
export function shouldHandleSocketIOMessage(message: MessageEvent<unknown>) {
    return typeof message.data === "string" && message.data.startsWith("42[");
}

export function parseSocketIOMessage(message: MessageEvent<unknown>) {
    const dataString = typeof message.data === "string" ? message.data : "";
    // Only match "42" at the beginning of the string, then any array after it
    // 42[messageType,messageData] -- match and extract only the second value (message data) as 'messageDataJson'
    const socketIoMessagePattern = /^42\["[^"]+",(?<messageDataJson>.+)\]$/;
    const dataMatch = dataString.match(socketIoMessagePattern);
    const messageDataJson = dataMatch?.groups?.["messageDataJson"];
    if (!messageDataJson) {
        throw new Error("Failed to parse socket.io message");
    }
    return JSON.parse(messageDataJson) as ExpertWorkspaceWebSocketMessagePayload;
}
